// v0.6.11 (audit Tier 3 item 1) — Per-row resolver for a B2C service to
// a consumer OUTSIDE the EU, flagged ART59_SERVICE_CATEGORY_REQUIRED.
//
// Art 45 taxes such a supply where the SUPPLIER is; Art 59's closed list
// (a)-(h) moves it to the customer and out of the Community. Nothing on
// the row says which applies, and both defaults ship a wrong return, so
// the row is held until the operator names the kind of service.
//
// Same shape as RefundChannelResolver: a closed-set picker plus a reason,
// the backend records the signal and an audit decision, and the next
// re-run (one for every answer, SavedAnswersBar) has the classifier honour it. The labels are the operator's
// words, not the Directive's numbering — the article reference is shown
// only as a quiet suffix so the audit trail stays legible without making
// the choice read like a legal exam.
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { resolveArt59Category } from "../../lib/api";
import { errorMessage } from "../../lib/errorMessage";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { toast } from "../ui/sonner";
import { PresetReasonSelect } from "../PresetReasonSelect";
import { ART59_CATEGORY_REASONS } from "../../lib/reviewReasonPresets";

// Kept in step with backend/adapters/client_template_schema.py's
// SUPPLY_TYPE_LABEL_TO_ENUM (same labels) and with
// engine/art59_service_categories.py, which owns the codes and the
// per-category citations. A jest test pins the value list.
//
// v0.6.11, second pass — these eight are now SUPPLY TYPES, picked in the
// template's Supply type column, so a new upload never reaches this
// resolver. It exists for rows uploaded BEFORE those types did, whose
// generic "Professional service" cannot be read as "none of Art 59's
// categories" because the distinction did not exist when it was chosen.
export const ART59_CATEGORIES = [
  { value: "LICENCES_AND_RIGHTS", label: "Copyright / licences", art: "59(a)" },
  { value: "ADVERTISING", label: "Advertising", art: "59(b)" },
  { value: "CONSULTANCY_AND_INFORMATION", label: "Consultancy or information", art: "59(c)" },
  { value: "REFRAIN_FROM_ACTIVITY", label: "Refraining from an activity", art: "59(d)" },
  { value: "BANKING_FINANCIAL_INSURANCE", label: "Banking, financial or insurance", art: "59(e)" },
  { value: "STAFF_SUPPLY", label: "Supplying staff", art: "59(f)" },
  { value: "MOVABLE_PROPERTY_HIRE", label: "Hiring out equipment", art: "59(g)" },
  { value: "UTILITY_NETWORK_ACCESS", label: "Gas / electricity network access", art: "59(h)" },
  { value: "NONE_OF_THESE", label: "None of these — an ordinary service", art: "45" },
];

export const Art59CategoryResolver = ({
  tid, calcId, calcFrozen, acked, onResolved, hideChip = false,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("");
  const [reason, setReason] = useState("");
  const [preset, setPreset] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const reset = () => { setCategory(""); setReason(""); setPreset(""); setErr(null); };

  const cleaned = reason.trim();
  const valid = !!category && cleaned.length >= 8 && !/[<>]/.test(cleaned);

  // The consequence of the pick, shown before it is made — the operator
  // is choosing between charging their own VAT and charging none, and
  // that is the fact worth surfacing, not the article number.
  const picked = ART59_CATEGORIES.find((c) => c.value === category);
  const outcome = !picked
    ? null
    : picked.value === "NONE_OF_THESE"
      ? t("ui.review_grouped.a59_outcome_home", {
        defaultValue: "Taxed in your own country, at your own rate.",
      })
      : t("ui.review_grouped.a59_outcome_out", {
        defaultValue: "Taxed where the customer is — no VAT due.",
      });

  if (acked) {
    return (
      <>
        {hideChip ? (
          <span data-testid={`rg-done-${tid}`} data-resolved="true" className="text-xs text-emerald-700 dark:text-emerald-300">
            ✓ {t("ui.review_grouped.row_resolved_label", { defaultValue: "Reviewed" })}
          </span>
        ) : (
        <span
          data-testid={`rg-txn-${tid}`}
          data-resolved="true"
          className="px-2 py-1 rounded border bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-950/40 dark:border-emerald-800 dark:text-emerald-200 truncate text-[10px] tabular-nums"
          title={tid}
        >
          ✓ {tid.slice(0, 12)}…
        </span>
        )}
      </>
    );
  }

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true); setErr(null);
    try {
      await resolveArt59Category(calcId, {
        txn_id: tid, category, reason: cleaned,
      });
      setOpen(false); reset();
      toast.success(
        t("ui.review_grouped.a59_toast_ok", { defaultValue: "Service kind saved — figures change at the next re-run" }),
        { description: `${tid.slice(0, 12)}… → ${picked ? picked.label : category}` },
      );
      // A1-105 — saved for the one re-run, like every Step 4 answer
      if (onResolved) await onResolved();
    } catch (e) {
      const msg = errorMessage(e);
      setErr(msg);
      toast.error(
        t("ui.review_grouped.a59_toast_fail", { defaultValue: "Could not save the service kind" }),
        { description: msg });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="flex items-center gap-2 flex-wrap">
        {!hideChip && (
        <span
          data-testid={`rg-txn-${tid}`}
          data-resolved="false"
          className="px-2 py-1 rounded border bg-slate-100 border-slate-200 text-slate-700 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200 truncate text-[10px] tabular-nums"
          title={tid}
        >
          {tid.slice(0, 12)}…
        </span>
        )}
        {!calcFrozen && (
          <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid={`a59-resolve-open-${tid}`}
                className="text-[10px] tabular-nums px-2 py-1 rounded border border-red-300 dark:border-red-700 hover:bg-red-100 dark:hover:bg-red-900/40 text-red-700 dark:text-red-200"
              >
                {t("ui.review_grouped.a59_resolve_btn", { defaultValue: "Pick service kind →" })}
              </button>
            </PopoverTrigger>
            <PopoverContent
              data-testid={`a59-resolve-popover-${tid}`}
              className="w-96 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 space-y-2"
            >
              <div className="text-[11px] font-bold text-slate-700 dark:text-slate-200">
                {t("ui.review_grouped.a59_resolve_title", { defaultValue: "What kind of service was this?" })}
              </div>
              <div className="text-[10px] text-slate-500 dark:text-slate-400 leading-snug">
                {t("ui.review_grouped.a59_resolve_help", {
                  defaultValue:
                    "Sold to a consumer outside the EU. The kinds listed below are taxed where the customer is, so no VAT is due. Anything else is taxed where you are, at your own rate.",
                })}
              </div>
              <select
                data-testid={`a59-resolve-category-${tid}`}
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 rounded px-2 py-1 text-xs"
              >
                <option value="">
                  {t("ui.review_grouped.a59_category_placeholder", { defaultValue: "Select the kind of service…" })}
                </option>
                {ART59_CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label} · Art {c.art}
                  </option>
                ))}
              </select>
              {outcome && (
                <div
                  data-testid={`a59-resolve-outcome-${tid}`}
                  className="text-[10px] rounded px-2 py-1 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200"
                >
                  {outcome}
                </div>
              )}
              {/* v0.6.12 A5-27 — pick, don't write. */}
              <PresetReasonSelect
                testid={`a59-resolve-reason-preset-${tid}`}
                presets={ART59_CATEGORY_REASONS}
                preset={preset}
                onPick={(p, v) => { setPreset(p); setReason(v); }}
              />
              {err && (
                <div data-testid={`a59-resolve-error-${tid}`} className="text-[10px] text-red-700 dark:text-red-300">
                  {err}
                </div>
              )}
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  data-testid={`a59-resolve-cancel-${tid}`}
                  onClick={() => { setOpen(false); reset(); }}
                  className="text-[10px] underline text-slate-600 dark:text-slate-300"
                >
                  {t("ui.review_grouped.a59_cancel", { defaultValue: "Cancel" })}
                </button>
                <button
                  type="button"
                  data-testid={`a59-resolve-submit-${tid}`}
                  data-resolution-class="self-resolve"
                  data-resolution-endpoint="POST /api/runs/calc/{calc_id}/resolve-art59-category"
                  disabled={busy || !valid}
                  onClick={submit}
                  className="text-[10px] font-bold px-3 py-1 rounded bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white"
                >
                  {busy
                    ? t("ui.review_grouped.a59_submitting", { defaultValue: "Saving…" })
                    : t("ui.review_grouped.a59_submit", { defaultValue: "Save" })}
                </button>
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>
    </>
  );
};

export default Art59CategoryResolver;
